import React from "react";

import { isPast } from "date-fns";
import { startCase } from "lodash";
import clsx from "clsx";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Hidden,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  colors,
  makeStyles,
} from "@material-ui/core";

import { VehicleAssignment, VehicleType } from "@hubcapp/types";
import { formatDateShort, formatDistance } from "@ds-developer/frontend";
import Button from "@ds-developer/button";

import { getLatestVehicleMileage } from "../../../helpers";

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: colors.grey[50],
  },
  cardHeader: {
    backgroundColor: colors.grey[100],
    boxShadow: `inset 0px -1px 0px ${colors.grey[200]}`,
  },
  cardContent: {
    "&:last-child": {
      padding: 0,
    },
  },
  mileageDistance: {
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "0.4px",
  },

  cell: {
    padding: theme.spacing(3, 2),
  },
  lastRowCell: { borderBottom: 0 },
  alertText: {
    color: theme.palette.error.main,
  },
}));

const VehicleCard: React.FC<
  VehicleAssignment & {
    onUpdateClick?: (id: string) => void;
    onUpdateInsuranceClick?: (id: string) => void;
  }
> = ({
  id,
  vehicle: {
    manufacturer,
    model,
    transmissionType,
    fuelType,
    registration,
    mileage,
    vehicleType,
    insurance,
  },
  onUpdateClick = () => {},
  onUpdateInsuranceClick = () => {},
}) => {
  const classes = useStyles();

  const VehicleRow: React.FC<{
    title: string;
    content: string;
    id: string;
    isAlertText?: boolean;
    showButton?: boolean;
    buttonText?: string;
    onButtonClick?: () => void;
  }> = ({
    content,
    title,
    id,
    isAlertText,
    showButton,
    buttonText,
    onButtonClick,
  }) => (
    <TableRow>
      <TableCell
        className={classes.cell}
        colSpan={1}
        data-test={`vehicle-detail-label-${id}`}
      >
        {title}
      </TableCell>
      <TableCell className={classes.cell} colSpan={showButton ? 2 : 3}>
        <Typography
          variant="subtitle2"
          data-test={`vehicle-detail-value-${id}`}
          className={isAlertText ? classes.alertText : undefined}
        >
          {content}
        </Typography>
      </TableCell>
      {showButton ? (
        <TableCell align="right" className={classes.cell} colSpan={1}>
          <Button
            color="primary"
            variant="outlined"
            id="vehicle-row-button"
            onClick={onButtonClick}
            data-test="vehicle-detail-row-button"
          >
            {buttonText}
          </Button>
        </TableCell>
      ) : (
        <React.Fragment />
      )}
    </TableRow>
  );

  const latestMileage = mileage ? getLatestVehicleMileage(mileage) : undefined;
  return (
    <Box mt={3} mb={3}>
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title={`Reg ${registration}`}
          data-test="driving-details-header"
        />
        <CardContent className={classes.cardContent}>
          <Table>
            <TableBody>
              <VehicleRow
                title="Make & Model"
                content={`${manufacturer} ${model}`}
                id="make"
              />
              <VehicleRow
                title="Fuel | Gears"
                content={`${startCase(fuelType.toLowerCase())} | ${startCase(
                  transmissionType.toLowerCase(),
                )} gearbox`}
                id="fuel"
              />
              {vehicleType === VehicleType.Business ? (
                <TableRow>
                  <TableCell
                    className={clsx(classes.cell, classes.lastRowCell)}
                    data-test="vehicle-detail-label-mileage"
                  >
                    Mileage
                  </TableCell>
                  <TableCell
                    className={clsx(classes.cell, classes.lastRowCell)}
                  >
                    <Typography
                      variant="subtitle2"
                      data-test="vehicle-detail-value-mileage"
                    >
                      {latestMileage ? latestMileage.value : "N/A"}
                    </Typography>
                  </TableCell>
                  <Hidden xsDown>
                    <TableCell
                      align="center"
                      className={clsx(classes.cell, classes.lastRowCell)}
                    >
                      <Typography className={classes.mileageDistance}>
                        {latestMileage &&
                          formatDistance(
                            new Date(latestMileage.captureDate),
                            new Date(Date.now()),
                            {
                              addSuffix: true,
                            },
                          )}
                      </Typography>
                    </TableCell>
                  </Hidden>
                  <TableCell
                    align="right"
                    className={clsx(classes.cell, classes.lastRowCell)}
                  >
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={() => onUpdateClick(id)}
                      data-test="update-mileage-button"
                    >
                      Update
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                <VehicleRow
                  title="Insurance expires"
                  content={
                    insurance?.endDate
                      ? formatDateShort(new Date(insurance?.endDate))
                      : "-"
                  }
                  id="leaseFrom"
                  isAlertText={isPast(
                    insurance?.endDate
                      ? new Date(insurance?.endDate)
                      : new Date(1970, 0, 1),
                  )}
                  buttonText="Update"
                  showButton
                  onButtonClick={() => {
                    onUpdateInsuranceClick && onUpdateInsuranceClick(id);
                  }}
                />
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VehicleCard;
